import type { CarReport, ProblemDetails, VehicleDefectProfile, VehicleRarity } from './types'
import { usageHeaders } from './usage'

/**
 * Why a report could not be shown. The distinction matters to the user: "we do not know this
 * plate" and "we cannot reach the RDW right now" call for different next steps, and the API is
 * careful to tell them apart — it answers 503 rather than 404 when the registration is
 * unreadable, precisely so a client does not tell somebody their car does not exist.
 */
export type ReportErrorKind =
  | 'invalid-plate'
  | 'not-found'
  | 'rate-limited'
  | 'unavailable'
  | 'network'
  | 'unknown'

export class ReportError extends Error {
  readonly kind: ReportErrorKind
  /** Seconds to wait before retrying, when the API said so. */
  readonly retryAfterSeconds?: number

  constructor(kind: ReportErrorKind, message: string, retryAfterSeconds?: number) {
    super(message)
    this.name = 'ReportError'
    this.kind = kind
    this.retryAfterSeconds = retryAfterSeconds
  }
}

/**
 * Fetches one car report.
 *
 * The request goes to `/api/report/{plate}` on this same origin. In production that is the Static
 * Web Apps managed function, which attaches the access token; in development the Vite dev server
 * rewrites it onto the API directly. Either way no credential is ever present in the browser.
 *
 * `viewId` and the landing facts travel as headers, so the API can count the lookup against the
 * page view it belongs to; see `usage.ts`.
 */
export function fetchCarReport(plate: string, signal?: AbortSignal, viewId?: string): Promise<CarReport> {
  return getJson<CarReport>(`/api/report/${encodeURIComponent(plate)}`, signal, usageHeaders(viewId))
}

/**
 * Fetches how common this vehicle's model is.
 *
 * A second request rather than part of the report, because the API keeps it separate: the count
 * costs the RDW around a second and a half, several times the report itself. Call it once the
 * report is on screen — never before it.
 */
export function fetchVehicleRarity(plate: string, signal?: AbortSignal): Promise<VehicleRarity> {
  return getJson<VehicleRarity>(`/api/rarity/${encodeURIComponent(plate)}`, signal)
}

/**
 * Fetches what vehicles of this model are usually pulled up on at the APK.
 *
 * The slowest thing the API serves — a cache miss costs it a dozen RDW requests, because the defect
 * dataset has no model column and the cohort has to be assembled a batch of plates at a time. Like
 * rarity it is a second request, and for the same reason, only more so: call it once the report is
 * on screen.
 */
export function fetchVehicleDefectProfile(
  plate: string,
  signal?: AbortSignal,
): Promise<VehicleDefectProfile> {
  return getJson<VehicleDefectProfile>(`/api/defect-profile/${encodeURIComponent(plate)}`, signal)
}

const WARM_UP_KEY = 'autochecken.api-warmed'

/**
 * Asks the API to start up while the visitor is still typing a plate.
 *
 * The API scales to zero and a cold start takes 20–30 s, so the home page calls this on load and
 * that time overlaps with reading and typing instead of following the search. Nothing waits for it
 * and nothing it returns is read; a failure just means the report request pays the cold start.
 *
 * Once per browser session: a replica stays up for five minutes after its last request, so a
 * second call from the same visitor almost never finds it asleep. Storage can be unavailable (a
 * private window, blocked site data), and that must not cost the page anything, so a throw there
 * simply means calling it again.
 */
export function warmUpApi(): void {
  try {
    if (sessionStorage.getItem(WARM_UP_KEY)) {
      return
    }
    sessionStorage.setItem(WARM_UP_KEY, '1')
  } catch {
    // Storage unavailable; warm up anyway.
  }

  fetch('/api/warmup', { cache: 'no-store' }).catch(() => {})
}

/** One same-origin GET, with every failure turned into a `ReportError` the pages can describe. */
async function getJson<T>(
  path: string,
  signal?: AbortSignal,
  headers: Record<string, string> = {},
): Promise<T> {
  let response: Response

  try {
    response = await fetch(path, {
      headers: { Accept: 'application/json', ...headers },
      signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error
    }

    throw new ReportError('network', 'De verbinding met AutoChecken is verbroken.')
  }

  if (response.ok) {
    return (await response.json()) as T
  }

  throw await toReportError(response)
}

async function toReportError(response: Response): Promise<ReportError> {
  const problem = await readProblemDetails(response)
  const retryAfter = Number.parseInt(response.headers.get('Retry-After') ?? '', 10)

  switch (response.status) {
    case 400:
      return new ReportError('invalid-plate', problem?.detail ?? 'Dit is geen geldig Nederlands kenteken.')
    case 404:
      return new ReportError('not-found', problem?.detail ?? 'De RDW kent dit kenteken niet.')
    case 429:
      return new ReportError(
        'rate-limited',
        'Er worden op dit moment te veel kentekens opgevraagd.',
        Number.isNaN(retryAfter) ? undefined : retryAfter,
      )
    case 502:
    case 503:
    case 504:
      return new ReportError(
        'unavailable',
        problem?.detail ?? 'De gegevens van de RDW zijn tijdelijk niet beschikbaar.',
        Number.isNaN(retryAfter) ? undefined : retryAfter,
      )
    default:
      return new ReportError('unknown', problem?.detail ?? 'Er ging iets mis bij het ophalen van dit rapport.')
  }
}

/** Reads the problem document, tolerating a body that is empty or not JSON at all. */
async function readProblemDetails(response: Response): Promise<ProblemDetails | undefined> {
  try {
    const body = await response.text()
    return body ? (JSON.parse(body) as ProblemDetails) : undefined
  } catch {
    return undefined
  }
}
